import { lazy, Suspense, useEffect, useState } from 'react';
import useTabAttention from './hooks/useTabAttention.js';
import Header from './components/Header.jsx';
import LandingHeader from './components/LandingHeader.jsx';
import AnnouncementBar from './components/AnnouncementBar.jsx';
import Footer from './components/Footer.jsx';
import CookieBanner from './components/CookieBanner.jsx';
import MobileCtaBar from './components/MobileCtaBar.jsx';
import RouteFallback from './components/RouteFallback.jsx';
import useFooterBottomHeight from './hooks/useFooterBottomHeight.js';
import useSmoothScroll, { getLenis } from './hooks/useSmoothScroll.js';
import useImageReveal from './hooks/useImageReveal.js';
import useConversionTracking from './hooks/useConversionTracking.js';
import useLandingConsent from './hooks/useLandingConsent.js';
import { initIntegrations } from './utils/integrations.js';

const HomePage = lazy(() => import('./HomePage.jsx'));
const NotFound = lazy(() => import('./components/NotFound.jsx'));
const TermsOfService = lazy(() => import('./components/TermsOfService.jsx'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy.jsx'));
const Contact = lazy(() => import('./components/Contact.jsx'));
const ContactSales = lazy(() => import('./components/ContactSales.jsx'));
const BookDemo = lazy(() => import('./components/BookDemo.jsx'));
const ThankYou = lazy(() => import('./components/ThankYou.jsx'));
const About = lazy(() => import('./components/About.jsx'));
const PricingPlan = lazy(() => import('./components/PricingPlan.jsx'));
const Changelog = lazy(() => import('./components/Changelog.jsx'));
const Faqs = lazy(() => import('./components/Faqs.jsx'));
const SetupGuide = lazy(() => import('./components/SetupGuide.jsx'));
const BlogIndex = lazy(() => import('./components/BlogIndex.jsx'));
const BlogDetail = lazy(() => import('./components/BlogDetail.jsx'));
const Features = lazy(() => import('./components/Features.jsx'));
const FeatureDetail = lazy(() => import('./components/FeatureDetail.jsx'));
const CaseStudies = lazy(() => import('./components/CaseStudies.jsx'));
const CaseStudyDetail = lazy(() => import('./components/CaseStudyDetail.jsx'));
const Integrations = lazy(() => import('./components/Integrations.jsx'));
const Security = lazy(() => import('./components/Security.jsx'));
const CookieSettings = lazy(() => import('./components/CookieSettings.jsx'));
const ROICalculator = lazy(() => import('./components/ROICalculator.jsx'));
const CompareNativeShopify = lazy(() => import('./components/CompareNativeShopify.jsx'));
const Alternatives = lazy(() => import('./components/Alternatives.jsx'));
const Glossary = lazy(() => import('./components/Glossary.jsx'));
const HelpIndex = lazy(() => import('./components/HelpIndex.jsx'));
const HelpDetail = lazy(() => import('./components/HelpDetail.jsx'));

const LANDING_PATHS = new Set(['/book-a-demo', '/book-a-demo-today', '/demo']);

function AttentionWatcher() {
  // Runs the tab-title swap when the visitor switches away. Renders nothing.
  useTabAttention();
  return null;
}

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPathname(window.location.pathname);
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  return pathname;
}

export default function App() {
  useFooterBottomHeight();
  useSmoothScroll();
  const pathname = usePathname();
  useImageReveal(pathname);

  // Standalone landing pages render inside the app shell but without any of its
  // chrome — no nav, no footer, no sticky mobile CTA. Keeping them on the same
  // shell is what lets them share the modal, consent and tracking providers.
  const isLanding = LANDING_PATHS.has(pathname);

  // MUST stay above initIntegrations for the same reason initIntegrations stays
  // above useConversionTracking: this is what writes the auto-granted consent
  // outside the EU, and initIntegrations only installs gtag if a consent record
  // already exists when it runs. One tick later and the first page_view lands
  // before the Ads tag does.
  useLandingConsent(isLanding);

  // MUST stay above useConversionTracking. Effects flush in the order their
  // hooks are declared, and useConversionTracking fires the first page_view
  // immediately on mount. page_view is a Google Ads conversion action, so if the
  // integrations haven't installed window.gtag by then, that conversion is
  // dropped — losing every single-page (bounced) session's landing page view.
  // GTM/GA4 are unaffected either way, since GTM replays whatever is already in
  // dataLayer when the container loads.
  useEffect(() => initIntegrations(), []);

  useConversionTracking(pathname);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    // Deep-link to an in-page anchor (e.g. /alternatives#revize). Content is
    // lazy-loaded, so retry briefly until the target element exists.
    if (window.location.hash) {
      const id = decodeURIComponent(window.location.hash.slice(1));
      let tries = 0;
      let timer;
      const tryScroll = () => {
        const el = document.getElementById(id);
        if (el) {
          const lenis = getLenis();
          if (lenis) lenis.scrollTo(el, { offset: -90 });
          else el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else if (tries++ < 25) {
          timer = setTimeout(tryScroll, 100);
        }
      };
      tryScroll();
      return () => clearTimeout(timer);
    }

    const lenis = getLenis();
    if (lenis) lenis.scrollTo(0, { immediate: true });
    else window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname]);

  let page;
  if (pathname === '/' || pathname === '') page = <HomePage />;
  else if (pathname === '/terms' || pathname === '/terms-of-service') page = <TermsOfService />;
  else if (pathname === '/privacy' || pathname === '/privacy-policy') page = <PrivacyPolicy />;
  else if (pathname === '/contact') page = <Contact />;
  else if (pathname === '/contact-sales' || pathname === '/sales') page = <ContactSales />;
  else if (pathname === '/book-a-demo' || pathname === '/book-a-demo-today' || pathname === '/demo') page = <BookDemo />;
  else if (pathname === '/thank-you' || pathname === '/thanks') page = <ThankYou />;
  else if (pathname === '/about' || pathname === '/about-us') page = <About />;
  else if (pathname === '/pricing-plan' || pathname === '/pricing' || pathname === '/plans') page = <PricingPlan />;
  else if (pathname === '/changelog') page = <Changelog />;
  else if (pathname === '/faqs' || pathname === '/faq') page = <Faqs />;
  else if (pathname === '/setup-guide' || pathname === '/setup' || pathname === '/docs') page = <SetupGuide />;
  else if (pathname === '/blog' || pathname === '/blog/') page = <BlogIndex />;
  else if (pathname.startsWith('/blog/')) page = <BlogDetail slug={pathname.slice('/blog/'.length).replace(/\/$/, '')} />;
  else if (pathname === '/features' || pathname === '/features/') page = <Features />;
  else if (pathname.startsWith('/features/')) page = <FeatureDetail slug={pathname.slice('/features/'.length).replace(/\/$/, '')} />;
  else if (pathname === '/case-studies' || pathname === '/case-studies/') page = <CaseStudies />;
  else if (pathname.startsWith('/case-studies/')) page = <CaseStudyDetail slug={pathname.slice('/case-studies/'.length).replace(/\/$/, '')} />;
  else if (pathname === '/integrations') page = <Integrations />;
  else if (pathname === '/security') page = <Security />;
  else if (pathname === '/cookie-settings' || pathname === '/cookies') page = <CookieSettings />;
  else if (pathname === '/roi-calculator' || pathname === '/roi') page = <ROICalculator />;
  else if (pathname === '/compare/account-editor-vs-native-shopify') page = <CompareNativeShopify />;
  else if (pathname === '/alternatives') page = <Alternatives />;
  else if (pathname === '/glossary') page = <Glossary />;
  else if (pathname === '/help' || pathname === '/help/' || pathname === '/help-center') page = <HelpIndex />;
  else if (pathname.startsWith('/help/')) page = <HelpDetail slug={pathname.slice('/help/'.length).replace(/\/$/, '')} />;
  else page = <NotFound />;

  return (
    <>
      <a href="#main" className="skip-link">Skip to content</a>
      {/* BFCM offer bar on every page except the chrome-free demo booking pages */}
      {!isLanding && <AnnouncementBar />}
      {isLanding ? <LandingHeader /> : <Header
        isHome={pathname === '/' || pathname === '' || pathname === '/pricing' || pathname === '/pricing-plan' || pathname === '/plans' || pathname === '/integrations' || pathname === '/roi-calculator' || pathname === '/roi' || pathname === '/setup-guide' || pathname === '/setup' || pathname === '/docs'}
        solidDark={pathname === '/pricing' || pathname === '/pricing-plan' || pathname === '/plans' || pathname === '/integrations' || pathname === '/roi-calculator' || pathname === '/roi' || pathname === '/setup-guide' || pathname === '/setup' || pathname === '/docs'}
      />}
      <div className="page">
        {/* data-suspense: the prerender marks this Suspense boundary so it hydrates */}
        <main id="main" tabIndex={-1} className="aehome" data-suspense>
          <Suspense fallback={<RouteFallback />}>{page}</Suspense>
        </main>
        {!isLanding && <Footer />}
      </div>
      <CookieBanner />
      {!isLanding && <MobileCtaBar />}
      <AttentionWatcher />
    </>
  );
}
